import { useCallback, useState } from "react";
import appLogger from "../../infra/logger/appLogger";
import { ABORT_ERROR_MESSAGE } from "../conversation";
import { useAppDispatch, useAppSelector } from "../hook";
import { useTranslation } from "../localization";
import { ModerationFlows, RateLimitFlows, useImageErrors, useModerationErrors, useRateLimitErrors } from "../miscellaneous";
import { editAvatarImageThunk, generateAvatarImageThunk, saveAvatarImageThunk } from "./characterSlice";
import { CharacterFlow } from "./types";
const avatarImageLogger = appLogger.withTag("AvatarImage");
export const useAvatarImage = (props) => {
    const dispatch = useAppDispatch();
    const { loading, loadingType } = useAppSelector((state) => state.character);
    const [stateAvatarError, setStateAvatarError] = useState(null);
    const { getMessage: getRateLimitErrorMessage, getTitle: getRateLimitTitle } = useRateLimitErrors();
    const { getMessage: getModerationErrorMessage } = useModerationErrors();
    const { getMessage: getImageErrorMessage } = useImageErrors();
    const { t } = useTranslation();
    const handleImageError = useCallback((error, flowType, operationName) => {
        const apiError = error;
        const isAbortError = apiError.message === ABORT_ERROR_MESSAGE;
        if (isAbortError) {
            return {
                data: null,
                error: ABORT_ERROR_MESSAGE,
                errorTitle: undefined,
                errorPayload: apiError,
            };
        }
        const rateLimitErrorMessage = getRateLimitErrorMessage({
            errorCode: apiError?.errorCode,
            skipDefaultMessage: true,
            errorContext: {
                flow: RateLimitFlows.GENERATE_IMAGE,
            },
        });
        const rateLimitTitle = getRateLimitTitle({
            errorCode: apiError?.errorCode,
            errorContext: {
                flow: RateLimitFlows.GENERATE_IMAGE,
            },
        });
        const moderationErrorMessage = getModerationErrorMessage({
            errorCode: apiError?.errorCode,
            skipDefaultMessage: true,
            errorContext: {
                flow: ModerationFlows.GENERATE_IMAGE,
            },
        });
        const errorMessage = rateLimitErrorMessage || moderationErrorMessage || apiError?.message || t("error:fallbackError");
        const errorTitle = rateLimitTitle || undefined;
        setStateAvatarError({
            errorType: flowType,
            errorMessage: errorMessage,
            errorTitle: errorTitle,
            errorCode: apiError?.errorCode,
        });
        avatarImageLogger.error(`Error ${operationName}: ${errorMessage}`, apiError);
        return { data: null, error: errorMessage, errorTitle: errorTitle, errorPayload: apiError };
    }, [getRateLimitErrorMessage, getRateLimitTitle, getModerationErrorMessage, t]);
    const onUserGenerateAvatarImage = useCallback(async ({ prompt, negative_prompt, seed, image, model_style, prompt_image_style, abortSignal }) => {
        try {
            setStateAvatarError(null);
            const data = await dispatch(generateAvatarImageThunk({
                prompt,
                negative_prompt,
                seed,
                image,
                model_style,
                prompt_image_style,
                abortSignal,
                ...(props?.apiVersion && { apiVersion: props?.apiVersion }),
            })).unwrap();
            return { data, error: null, errorTitle: undefined, errorPayload: undefined };
        }
        catch (error) {
            return handleImageError(error, CharacterFlow.GENERATE_AVATAR_IMAGE, "generating avatar");
        }
    }, [dispatch, handleImageError, props?.apiVersion]);
    const onUserEditAvatarImage = useCallback(async ({ prompt, model, seed, referenceImage, abortSignal }) => {
        try {
            setStateAvatarError(null);
            const data = await dispatch(editAvatarImageThunk({
                prompt,
                model,
                seed,
                referenceImage,
                abortSignal,
            })).unwrap();
            return { data, error: null, errorPayload: undefined };
        }
        catch (error) {
            return handleImageError(error, CharacterFlow.EDIT_AVATAR_IMAGE, "editing avatar");
        }
    }, [dispatch, handleImageError, t]);
    const onUserSaveAvatarImage = useCallback(async ({ prompt, image_type, hash, fileSize, imageData, generation_ref }) => {
        try {
            setStateAvatarError(null);
            const data = await dispatch(saveAvatarImageThunk({
                prompt,
                image_type,
                hash,
                fileSize,
                imageData,
                generation_ref,
            })).unwrap();
            return { data, error: null, errorPayload: undefined };
        }
        catch (error) {
            const apiError = error;
            const errorCode = apiError?.errorCode || "";
            const moderationErrorMessage = getModerationErrorMessage({
                errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: ModerationFlows.UPLOAD_USER_IMAGE,
                },
            });
            const imageErrorMessage = getImageErrorMessage({
                errorCode,
                skipDefaultMessage: true,
            });
            const errorMessage = moderationErrorMessage || imageErrorMessage || t("error:fallbackError");
            setStateAvatarError({
                errorType: CharacterFlow.SAVE_AVATAR_IMAGE,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            avatarImageLogger.error(`Error saving avatar: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getImageErrorMessage, getModerationErrorMessage, t]);
    return {
        isLoading: loading,
        loadingType,
        errorType: stateAvatarError?.errorType || null,
        errorMessage: stateAvatarError?.errorMessage || null,
        errorTitle: stateAvatarError?.errorTitle || null,
        errorCode: stateAvatarError?.errorCode || null,
        onUserGenerateAvatarImage,
        onUserEditAvatarImage,
        onUserSaveAvatarImage,
    };
};
export default useAvatarImage;
